/*
 * This file is part of glowingblue/orell-facade.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';

/**
 * @see https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce#measuring-promos
 */
export default class LinkWrapper extends Component {
	oninit(vnode) {
		super.oninit(vnode);

		const gaAttrs = this.gaAttrs;
		if (gaAttrs) {
			const { id, name, creative, position } = gaAttrs;
			ga('ec:addPromo', {
				id,
				name,
				creative,
				position,
			});
			ga('send', 'pageview');
		}

		const ga4Attrs = this.ga4Attrs;
		if (ga4Attrs) {
			const { id, name, creative, position } = ga4Attrs;
			window.gtag('event', 'view_promotion', {
				items: [
					{
						promotion_id: id,
						promotion_name: name,
						creative_name: creative,
						location_id: position,
						item_id: id,
						item_name: name,
					},
				],
			});
		}
	}

	get gaAttrs() {
		const { banner } = this.attrs;
		if (typeof window.ga === 'function' && banner.ga()) {
			return banner.ga();
		} else {
			return false;
		}
	}

	get ga4Attrs() {
		const { banner } = this.attrs;
		if (typeof window.gtag === 'function' && banner.ga()) {
			return banner.ga();
		} else {
			return false;
		}
	}

	/**
	 * The root-relative path to hand to Mithril's router, or `null` when the click has to be left to
	 * the browser.
	 *
	 * `is_external` is ticked by hand by editors, so origin is compared here rather than trusted from
	 * it: an off-forum URL saved with the box unchecked reached `m.route.set()`, and `pushState()`
	 * throws `SecurityError` on a cross-origin URL. The flag remains a way to force a full page load
	 * for a same-origin URL Mithril does not serve.
	 *
	 * @return {string|null}
	 */
	get routePath() {
		const { url, is_external, new_tab } = this.attrs.banner.link();

		if (!url || is_external || new_tab) {
			return null;
		}

		let resolved;

		try {
			resolved = new URL(url, window.location.href);
		} catch (e) {
			return null;
		}

		if (resolved.origin !== window.location.origin) {
			return null;
		}

		return `${resolved.pathname}${resolved.search}${resolved.hash}`;
	}

	view(vnode) {
		const { url, new_tab } = this.attrs.banner.link();

		return (
			<Link
				className="LinkWrapper"
				href={url}
				external={this.routePath === null}
				target={new_tab ? '_blank' : ''}
				onclick={this.handleClick.bind(this)}
			>
				{vnode.children}
			</Link>
		);
	}

	handleClick(e) {
		e.stopPropagation();

		const gaAttrs = this.gaAttrs;
		if (gaAttrs) {
			const { id, name, creative, position } = gaAttrs;
			ga('ec:addPromo', {
				id,
				name,
				creative,
				position,
			});
			ga('ec:setAction', 'promo_click');
			ga('send', 'event', 'Internal Promotions', 'click', name);
		}

		const ga4Attrs = this.ga4Attrs;
		if (ga4Attrs) {
			const { id, name, creative, position } = ga4Attrs;
			window.gtag('event', 'select_promotion', {
				items: [
					{
						promotion_id: id,
						promotion_name: name,
						creative_name: creative,
						location_id: position,
						item_id: id,
						item_name: name,
					},
				],
			});
		}

		const routePath = this.routePath;

		// Plain anchors already navigate on their own, and a modified click asks the browser for a new
		// tab or window — the same distinction Mithril's own `route.Link` draws.
		if (routePath === null || !this.isPlainLeftClick(e)) {
			return;
		}

		// `m.route.Link`'s own handler would route the unrewritten href the moment ours returns.
		e.preventDefault();

		m.route.set(routePath);
	}

	isPlainLeftClick(e) {
		return (e.button === 0 || e.which === 0 || e.which === 1) && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;
	}
}
